import { useMutation } from '@tanstack/react-query'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type { Ticket } from '@core/Areas/Account/Pages/Activity/types'
import type {
  KambiTicketsToShare,
  SelectedKambiTickets,
  SelectedTickets,
  TicketsToShare,
} from '@core/Areas/BetLoop/Components/QuickShare/types'
import {
  postMultipleKambiSharesToBetLoop,
  postMultipleSharesToBetLoop,
} from '@core/Areas/BetLoop/helpers/api'
import type {
  BetLoopPostMultipleKambiSharesResponse,
  BetLoopPostMultipleSharesResponse,
} from '@core/Areas/BetLoop/helpers/api/types'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import { trackShareBetError } from '@core/Areas/ShareBet/helpers/analytics'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { handleShareToBetLoopMutateResponse } from '../helpers/handleShareToBetLoopMutateResponse'

export const usePostMultipleSharesToBetLoopMutation = ({
  ticketsToShare,
  kambiTicketsToShare,
  onDismiss,
  selectedTicketsRef,
  selectedKambiTicketsRef,
  setIsFirstShareModalOpen,
}: {
  ticketsToShare: TicketsToShare
  kambiTicketsToShare: KambiTicketsToShare
  selectedTicketsRef: React.MutableRefObject<SelectedTickets>
  selectedKambiTicketsRef: React.MutableRefObject<SelectedKambiTickets>
  onDismiss: () => void
  setIsFirstShareModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const [racingResponse, kambiResponse] = await Promise.all([
        ticketsToShare.size
          ? postMultipleSharesToBetLoop({
              ticketSequences: Array.from(ticketsToShare),
            })
          : Promise.resolve(undefined),

        kambiTicketsToShare.size
          ? postMultipleKambiSharesToBetLoop({
              bets: Array.from(kambiTicketsToShare),
            })
          : Promise.resolve(undefined),
      ])

      return { racingResponse, kambiResponse }
    },
    onSuccess: ({ racingResponse, kambiResponse }) => {
      handleShareToBetLoopMutateResponse({
        racingResponse,
        kambiResponse,
        selectedTickets: selectedTicketsRef.current,
        selectedKambiTickets: selectedKambiTicketsRef.current,
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.betLoopSharePendingTickets,
      })
      queryClient.invalidateQueries(betLoopPrincipleProfileQuery)
      queryClient.setQueriesData(
        {
          queryKey: [queryKeys.betLoopSharePendingTickets, 'racing'],
        },
        clearRacingCache(racingResponse)
      )
      queryClient.setQueriesData(
        {
          queryKey: [queryKeys.betLoopSharePendingTickets, 'kambi'],
        },
        clearKambiCache(kambiResponse)
      )
    },
    onMutate() {
      setIsFirstShareModalOpen(false)
      onDismiss()
    },
    onError: handleShareToBetLoopMutateError,
  })

  return { mutate, isPending }
}

// =============
// Local Helpers
// =============

function clearKambiCache(kambiResponse?: BetLoopPostMultipleKambiSharesResponse) {
  return function (currentCachedData: FetchPendingCouponsData): FetchPendingCouponsData {
    if (!currentCachedData) return currentCachedData

    return {
      data: currentCachedData.data.filter(
        cacheRecord =>
          !kambiResponse?.results.some(
            responseRecord =>
              responseRecord.success && responseRecord.betRef === cacheRecord.bets[0].betRef
          )
      ),
    }
  }
}

function clearRacingCache(racingResponse?: BetLoopPostMultipleSharesResponse): unknown {
  return function (currentCachedData: FetchPendingTicketsData): FetchPendingTicketsData {
    if (!currentCachedData) return currentCachedData

    return {
      data: currentCachedData.data.filter(
        cacheRecord =>
          !racingResponse?.results.some(
            responseRecord =>
              responseRecord.success && responseRecord.ticketSequence === cacheRecord.ticketSequence
          )
      ),
    }
  }
}

function handleShareToBetLoopMutateError(error: Error) {
  trackShareBetError({
    message: error.message,
    destination: 'Bet Loop',
    location: 'bet-loop',
    betType: undefined,
  })

  addToast({
    id: 'bet-loop-quick-share--error',
    timeoutMs: 6_000,
    title: 'Oops, unable to share to Bet Loop',
    icon: 'error',
    theme: 'dark',
  })
}

type FetchPendingTicketsData = {
  data: Ticket[]
}

type FetchPendingCouponsData = {
  data: KambiHistoryCoupon[]
}
